import { useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import PropertyPanel, { UIElementData } from './components/PropertyPanel'
import Toolbar from './components/Toolbar'
import './App.css'

function App() {
  const [elements, setElements] = useState<UIElementData[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dragOverInfo, setDragOverInfo] = useState<{ id: string; position: 'before' | 'after' } | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)

  // タッチとマウスの両方をサポートするセンサーを設定
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8, // 8pxドラッグしてから開始（誤動作防止）
    },
  })
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 200, // 200msのタッチでドラッグ開始（スクロールと区別）
      tolerance: 8,
    },
  })
  const sensors = useSensors(mouseSensor, touchSensor)

  const addElement = (type: 'textfield' | 'slider' | 'checkbox' | 'dropdown', index?: number) => {
    const newElement: UIElementData = {
      id: crypto.randomUUID(),
      type
    }
    if (index !== undefined && index >= 0 && index <= elements.length) {
      // 指定された位置に挿入
      const newElements = [...elements]
      newElements.splice(index, 0, newElement)
      setElements(newElements)
    } else {
      // 最後に追加（デフォルト動作）
      setElements([...elements, newElement])
    }
    setSelectedId(null) // 新しい要素追加時は選択を解除
  }

  const deleteElement = (id: string) => {
    setElements(elements.filter(el => el.id !== id))
    if (selectedId === id) {
      setSelectedId(null)
    }
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event

    if (!over) {
      setDragOverInfo(null)
      return
    }

    // ドラッグ中の要素自身の上にいる場合は何も表示しない
    if (active.id === over.id) {
      setDragOverInfo(null)
      return
    }

    // 要素のインデックスを取得して位置関係を判断
    const activeIndex = elements.findIndex(el => el.id === active.id)
    const overIndex = elements.findIndex(el => el.id === over.id)

    // ツールバーからのドラッグの場合（新規要素追加）
    if (active.id.toString().startsWith('toolbar-')) {
      // ツールバーからのドラッグは常に 'before' (要素の前に挿入)
      setDragOverInfo({
        id: over.id as string,
        position: 'before'
      })
      return
    }

    // 既存要素の並び替えの場合、インデックスで位置関係を判断
    if (activeIndex !== -1 && overIndex !== -1) {
      // activeIndex < overIndex: 上の要素を下に移動 → 下の要素の後ろに挿入
      // activeIndex > overIndex: 下の要素を上に移動 → 上の要素の前に挿入
      const position = activeIndex < overIndex ? 'after' : 'before'
      setDragOverInfo({
        id: over.id as string,
        position
      })
    } else {
      setDragOverInfo(null)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setDragOverInfo(null)
    setActiveId(null)

    if (!over) return

    const activeId = active.id as string
    const overId = over.id as string

    // Toolbarからのドラッグの場合（新規要素追加）
    if (activeId.startsWith('toolbar-')) {
      const type = activeId.replace('toolbar-', '') as 'textfield' | 'slider' | 'checkbox' | 'dropdown'

      if (overId === 'property-panel-empty') {
        // 空のパネルへのドロップ
        addElement(type)
      } else {
        // 既存要素の上へのドロップ
        const overIndex = elements.findIndex(el => el.id === overId)
        if (overIndex !== -1) {
          addElement(type, overIndex)
        } else {
          addElement(type)
        }
      }
      return
    }

    // 既存要素の並び替え
    if (activeId !== overId) {
      const oldIndex = elements.findIndex(el => el.id === activeId)
      const newIndex = elements.findIndex(el => el.id === overId)

      if (oldIndex !== -1 && newIndex !== -1) {
        setElements(arrayMove(elements, oldIndex, newIndex))
      }
    }
  }

  // ドラッグ中の要素のタイプを取得
  const getActiveElementType = () => {
    if (!activeId) return null
    if (activeId.startsWith('toolbar-')) {
      return activeId.replace('toolbar-', '')
    }
    const element = elements.find(el => el.id === activeId)
    return element?.type || null
  }

  // ドラッグオーバーレイのラベルを取得
  const getDragOverlayLabel = () => {
    const type = getActiveElementType()
    if (!type) return ''

    const labels: Record<string, string> = {
      'textfield': 'テキストフィールド',
      'slider': 'スライダー',
      'checkbox': 'チェックボックス',
      'dropdown': 'ドロップダウン'
    }
    return labels[type] || type
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="app">
        <header className="app-header">
          <h1>Blender Wireframe Maker</h1>
        </header>
        <div className="app-content">
          <Toolbar />
          <PropertyPanel
            elements={elements}
            selectedId={selectedId}
            onSelectElement={setSelectedId}
            onDeleteElement={deleteElement}
            dragOverInfo={dragOverInfo}
            activeId={activeId}
          />
        </div>
      </div>
      <DragOverlay>
        {activeId ? (
          <div className="drag-overlay-preview">
            {getDragOverlayLabel()}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default App
